// Customer ticket colours — the physical job-ticket colour scheme.
// Ported from TodayView.tsx (operator pass 1, 2026-04-28). These are now the
// SEED values for production.customer_colours (migration 097); the live map is
// fetched and edited in-app (see useCustomerColours + ColourSettingsDrawer).
// This constant is the fallback used before the query resolves so the board
// looks identical on first paint. Customers without a row fall back to a stable
// forme-hash palette.

export type BarStyle = { fill: string; text: string; border?: string };
export type ColourMap = Record<string, BarStyle>;

// The fixed set of physical job-bag colours operators choose from. The colour
// editor offers ONLY these (no free hex picker) so a bar's on-screen colour
// matches the bag colour on the floor. Hexes reuse the board's existing palette
// so already-mapped customers map straight onto a named colour. text/border are
// paired for legibility (white needs a border; yellow/brown take dark text).
export type JobBagColour = { name: string; fill: string; text: string; border: string | null };
export const JOB_BAG_COLOURS: JobBagColour[] = [
  { name: 'White',       fill: '#ffffff', text: '#1a1a1a', border: '#9ca3af' },
  { name: 'Pink',        fill: '#ec4899', text: '#ffffff', border: null },
  { name: 'Orange',      fill: '#f97316', text: '#ffffff', border: null },
  { name: 'Purple',      fill: '#dab1da', text: '#1a1a1a', border: null },
  { name: 'Blue',        fill: '#2563eb', text: '#ffffff', border: null },
  { name: 'Green',       fill: '#16a34a', text: '#ffffff', border: null },
  { name: 'Yellow',      fill: '#facc15', text: '#1a1a1a', border: null },
  { name: 'Light Brown', fill: '#c19a6b', text: '#1a1a1a', border: null },
  { name: 'Gray',        fill: '#64748b', text: '#ffffff', border: null },
];

// Reverse-map a saved fill hex to its job-bag colour name (null if it's a
// legacy/auto colour not in the standard set).
export function jobBagNameForFill(fill: string): string | null {
  const f = (fill || '').toLowerCase();
  return JOB_BAG_COLOURS.find((c) => c.fill.toLowerCase() === f)?.name ?? null;
}

export const CUSTOMER_COLOURS: ColourMap = {
  RCP: { fill: '#2563eb', text: 'white' },
  NESB: { fill: '#facc15', text: '#1a1a1a' },
  PTRS: { fill: '#facc15', text: '#1a1a1a' },
  ALE: { fill: '#facc15', text: '#1a1a1a' },
  NESW: { fill: '#16a34a', text: 'white' },
  AAR: { fill: '#16a34a', text: 'white' },
  SAPE: { fill: '#f97316', text: 'white' },
  SBF: { fill: '#f97316', text: 'white' },
  BKF: { fill: '#f97316', text: 'white' },
  KCA: { fill: '#ec4899', text: 'white' },
  KEL: { fill: '#ec4899', text: 'white' },
  MCA: { fill: '#64748b', text: 'white' },
  EXEL: { fill: '#64748b', text: 'white' },
  BDD: { fill: '#ffffff', text: '#1a1a1a', border: '#9ca3af' },
  ABA: { fill: '#ffffff', text: '#1a1a1a', border: '#9ca3af' },
  ICO: { fill: '#ffffff', text: '#1a1a1a', border: '#9ca3af' },
};

const FORME_PALETTE = [
  '#9333ea', '#2563eb', '#4338ca', '#be185d',
  '#16a34a', '#ea580c', '#db2777', '#0891b2',
  '#ca8a04', '#0d9488', '#7c3aed', '#dc2626',
];

function formeFallback(forme: string | null | undefined): BarStyle {
  if (!forme) return { fill: '#94a3b8', text: 'white' };
  let hash = 0;
  for (let i = 0; i < forme.length; i++) {
    hash = ((hash << 5) - hash + forme.charCodeAt(i)) | 0;
  }
  return { fill: FORME_PALETTE[Math.abs(hash) % FORME_PALETTE.length], text: 'white' };
}

export function barStyle(
  customerCode: string | null | undefined,
  forme: string | null | undefined,
  colourMap: ColourMap = CUSTOMER_COLOURS,
): BarStyle {
  if (customerCode && colourMap[customerCode]) return colourMap[customerCode];
  return formeFallback(forme);
}

// Auto-contrast for arbitrary hex (separation swatches): luma > 145 → dark text.
// Matches the prototype's contrast(hex).
export function contrastText(hex: string): '#1a1a1a' | '#fff' {
  const c = hex.replace('#', '');
  if (c.length < 6) return '#1a1a1a';
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 145 ? '#1a1a1a' : '#fff';
}
